import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import {
  AppDialogSection,
  AppDialog,
  AppDialogActions,
  AppDialogContent,
  AppDialogHeader,
  AppDialogTitle,
  AppDialogXBtn,
} from '@/UI/AppDialog/AppDialog';
import { I18N_KEYS } from '@/language/keys';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { CardRadioGroup, type CardRadioOption } from './CardRadioGroup';

const enum SelectedOptionEnum {
  MoreCredits = 'more_credits',
  UpgradeToPaidPlan = 'upgrade_to_paid_plan',
}

interface CreditsOrPricingModalProps {
  open: boolean;
  onClose: () => void;
  setShowGetMoreCreditsModal: (open: boolean) => void;
  setShowPricingPlansModal: (open: boolean) => void;
}

function CreditsOrPricingModal({
  open,
  onClose,
  setShowGetMoreCreditsModal,
  setShowPricingPlansModal,
}: CreditsOrPricingModalProps) {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const [selectedOption, setSelectedOption] = useState<SelectedOptionEnum>(SelectedOptionEnum.MoreCredits);

  const planOptions: CardRadioOption<SelectedOptionEnum>[] = [
    {
      value: SelectedOptionEnum.MoreCredits,
      title: t(I18N_KEYS.PAYMENTS.CREDITS_OR_PRICING_MODAL.CTA_CREDITS.TITLE),
      subtitle: t(I18N_KEYS.PAYMENTS.CREDITS_OR_PRICING_MODAL.CTA_CREDITS.SUBTITLE),
    },
    {
      value: SelectedOptionEnum.UpgradeToPaidPlan,
      title: t(I18N_KEYS.PAYMENTS.CREDITS_OR_PRICING_MODAL.CTA_PRICING.TITLE),
      subtitle: t(I18N_KEYS.PAYMENTS.CREDITS_OR_PRICING_MODAL.CTA_PRICING.SUBTITLE),
    },
  ];

  const handleGetMoreCredits = () => {
    track(
      'Credits_or_pricing_modal - Clicked_get_more_credits',
      {
        context: 'Payments',
      },
      TrackTypeEnum.BI,
    );
    setShowGetMoreCreditsModal(true);
    onClose();
  };

  const handleUpgradeToPaidPlan = () => {
    track(
      'Credits_or_pricing_modal - Clicked_upgrade_to_paid_plan',
      {
        context: 'Payments',
      },
      TrackTypeEnum.BI,
    );
    setShowPricingPlansModal(true);
    onClose();
  };

  const handleContinueClick = () => {
    if (selectedOption === SelectedOptionEnum.MoreCredits) {
      handleGetMoreCredits();
    } else if (selectedOption === SelectedOptionEnum.UpgradeToPaidPlan) {
      handleUpgradeToPaidPlan();
    }
  };

  return (
    <AppDialog open={open} onClose={onClose} width="md" slotProps={{ paper: { sx: { backgroundColor: token.bg } } }}>
      <AppDialogSection>
        <AppDialogHeader component="div" sx={{ p: 3, pt: 5, pb: 0 }}>
          <AppDialogTitle>{t(I18N_KEYS.PAYMENTS.CREDITS_OR_PRICING_MODAL.TITLE)}</AppDialogTitle>
          <AppDialogXBtn sx={{ position: 'absolute', top: 12, right: 12 }} onClick={onClose} />
        </AppDialogHeader>
        <AppDialogContent sx={{ px: 3, pb: 0, '&&': { pt: 1.5 } }}>
          <CardRadioGroup
            options={planOptions}
            selected={selectedOption}
            onSelect={setSelectedOption}
            groupLabel={t(I18N_KEYS.PAYMENTS.CREDITS_OR_PRICING_MODAL.TITLE)}
            title={t(I18N_KEYS.PAYMENTS.CREDITS_OR_PRICING_MODAL.SUBTITLE)}
          />
        </AppDialogContent>
        <AppDialogActions sx={{ justifyContent: 'flex-end', px: 3, pb: 3, pt: 4 }}>
          <ButtonContained size="small" mode="outlined" onClick={onClose}>
            {t(I18N_KEYS.PAYMENTS.UPGRADE_MODAL.CTA_CANCEL)}
          </ButtonContained>
          <ButtonContained size="small" onClick={handleContinueClick}>
            {t(I18N_KEYS.PAYMENTS.CREDITS_OR_PRICING_MODAL.CTA_CONFIRM)}
          </ButtonContained>
        </AppDialogActions>
      </AppDialogSection>
    </AppDialog>
  );
}

export default CreditsOrPricingModal;
